import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import ApiService from '../../../_services/ApiService';

interface ConflictingSlotDetails {
  slotId: number;
  slotDate?: string | null;
  slotStartTime?: string | null;
  slotEndTime?: string | null;
  templateId?: number | null;
  templateName?: string | null;
  instructorId?: number | null;
  instructorName?: string | null;
  roomId?: number | null;
  roomName?: string | null;
  courseName?: string | null;
  topicName?: string | null;
}

interface ConflictViolation {
  ruleKey: string;
  ruleLabel: string;
  severity: 'blocker' | 'warning';
  slotId: number;
  slotDate: string;
  slotStartTime: string;
  slotEndTime: string;
  templateId: number;
  templateName: string;
  instructorId: number | null;
  instructorName: string | null;
  roomId: number | null;
  roomName: string | null;
  courseName: string | null;
  topicName: string | null;
  message: string;
  conflictingSlotId?: number;
  conflicting?: ConflictingSlotDetails;
}

interface ReportSummary {
  totalViolations: number;
  blockers: number;
  warnings: number;
  byRule: Record<string, number>;
}

interface ReportPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

interface TemplateOption {
  id: number;
  name: string;
}

interface ConflictReportProps {
  templates?: TemplateOption[];
}

const ConflictReport: React.FC<ConflictReportProps> = ({ templates = [] }) => {
  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);

  // Results state
  const [violations, setViolations] = useState<ConflictViolation[]>([]);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [pagination, setPagination] = useState<ReportPagination>({ page: 1, limit: 100, totalCount: 0, totalPages: 0 });
  const [executionTimeMs, setExecutionTimeMs] = useState<number | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasRun, setHasRun] = useState(false);

  // Inline filters
  const [filterSeverity, setFilterSeverity] = useState<'all' | 'blocker' | 'warning'>('all');
  const [filterRule, setFilterRule] = useState('');

  // Get today and 30 days from now as default range
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const runReport = async (page = 1) => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays < 0) {
      setError('End date must be after start date.');
      return;
    }
    if (diffDays > 365) {
      setError('Date range cannot exceed 1 year.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await ApiService.runConflictReport({
        startDate,
        endDate,
        templateIds: selectedTemplateIds.length > 0 ? selectedTemplateIds : undefined,
        page,
        limit: 100,
      });

      if (res.data?.success) {
        setViolations(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(res.data.pagination || { page: 1, limit: 100, totalCount: 0, totalPages: 0 });
        setExecutionTimeMs(res.data.metadata?.executionTimeMs ?? null);
        setHasRun(true);
      } else {
        const e = res.data?.error;
        setError(typeof e === 'string' ? e : e?.message || 'Failed to run report.');
      }
    } catch (err: any) {
      const e = err.response?.data?.error;
      setError(typeof e === 'string' ? e : e?.message || err.message || 'Failed to run report.');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setLoading(true);
    try {
      const res = await ApiService.runConflictReport({
        startDate,
        endDate,
        templateIds: selectedTemplateIds.length > 0 ? selectedTemplateIds : undefined,
        exportAll: true,
      });

      if (!res.data?.success) {
        setError('Export failed.');
        return;
      }

      const allViolations: ConflictViolation[] = res.data.data || [];
      const wb = XLSX.utils.book_new();

      // Sheet 1: Violations
      const violationData = allViolations.map((v) => {
        const c = v.conflicting;
        return {
          Severity: v.severity.toUpperCase(),
          Rule: v.ruleLabel,
          'Slot ID': v.slotId || '',
          Date: v.slotDate,
          'Start Time': v.slotStartTime,
          'End Time': v.slotEndTime,
          Template: v.templateName,
          Instructor: v.instructorName || '',
          Room: v.roomName || '',
          Course: v.courseName || '',
          Topic: v.topicName || '',
          'Conflicting Slot ID': v.conflictingSlotId || '',
          'Conflicting Date': c?.slotDate || '',
          'Conflicting Start': c?.slotStartTime || '',
          'Conflicting End': c?.slotEndTime || '',
          'Conflicting Template': c?.templateName || '',
          'Conflicting Instructor': c?.instructorName || '',
          'Conflicting Room': c?.roomName || '',
          'Conflicting Course': c?.courseName || '',
          'Conflicting Topic': c?.topicName || '',
          Message: v.message,
        };
      });

      const sheet = XLSX.utils.json_to_sheet(violationData);
      sheet['!cols'] = [
        { wch: 10 }, { wch: 25 }, { wch: 8 },
        { wch: 12 }, { wch: 10 }, { wch: 10 },
        { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
        { wch: 12 },
        { wch: 12 }, { wch: 10 }, { wch: 10 },
        { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
        { wch: 60 },
      ];
      XLSX.utils.book_append_sheet(wb, sheet, 'Violations');

      // Sheet 2: Summary
      const summaryObj = res.data.summary;
      if (summaryObj) {
        const summaryData = Object.entries(summaryObj.byRule || {}).map(([rule, count]) => ({
          Rule: rule,
          Count: count as number,
        }));
        summaryData.unshift(
          { Rule: 'Total Violations', Count: summaryObj.totalViolations },
          { Rule: 'Blockers', Count: summaryObj.blockers },
          { Rule: 'Warnings', Count: summaryObj.warnings },
          { Rule: '---', Count: 0 },
        );
        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        summarySheet['!cols'] = [{ wch: 30 }, { wch: 10 }];
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      }

      XLSX.writeFile(wb, `conflict-report-${startDate}-to-${endDate}.xlsx`);
    } catch (err: any) {
      setError('Export failed: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering of current page results
  const filteredViolations = useMemo(() => {
    let filtered = violations;
    if (filterSeverity !== 'all') {
      filtered = filtered.filter((v) => v.severity === filterSeverity);
    }
    if (filterRule) {
      filtered = filtered.filter((v) => v.ruleKey === filterRule);
    }
    return filtered;
  }, [violations, filterSeverity, filterRule]);

  // Available rule keys from current results for dropdown
  const availableRules = useMemo(() => {
    if (!summary?.byRule) return [];
    return Object.keys(summary.byRule).sort();
  }, [summary]);

  const handleTemplateToggle = (id: number) => {
    setSelectedTemplateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="stt-conflict-report">
      {/* Filter Bar */}
      <div className="stt-cr-filters">
        <div className="stt-cr-filter-row">
          <div className="stt-cr-filter-field">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || undefined}
            />
          </div>
          <div className="stt-cr-filter-field">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </div>
          <div className="stt-cr-filter-field stt-cr-filter-templates">
            <label>Templates (optional)</label>
            <select
              multiple
              value={selectedTemplateIds.map(String)}
              onChange={(e) => {
                const opts = Array.from(e.target.selectedOptions, (o) => Number(o.value));
                setSelectedTemplateIds(opts);
              }}
              style={{ minHeight: 36 }}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {selectedTemplateIds.length > 0 && (
              <button
                className="stt-cr-clear-btn"
                onClick={() => setSelectedTemplateIds([])}
                title="Clear selection"
              >
                Clear
              </button>
            )}
          </div>
          <div className="stt-cr-filter-actions">
            <button
              className="btn btn-primary"
              onClick={() => runReport(1)}
              disabled={loading || !startDate || !endDate}
            >
              {loading ? 'Running...' : 'Run Report'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="stt-cr-error">{typeof error === 'string' ? error : String(error)}</div>
      )}

      {/* Summary Cards */}
      {hasRun && summary && (
        <>
          <div className="stt-cr-summary">
            <div className="stt-cr-summary-card">
              <div className="stt-cr-card-value">{summary.totalViolations}</div>
              <div className="stt-cr-card-label">Total Violations</div>
            </div>
            <div className="stt-cr-summary-card stt-cr-card-blocker">
              <div className="stt-cr-card-value">{summary.blockers}</div>
              <div className="stt-cr-card-label">Blockers</div>
            </div>
            <div className="stt-cr-summary-card stt-cr-card-warning">
              <div className="stt-cr-card-value">{summary.warnings}</div>
              <div className="stt-cr-card-label">Warnings</div>
            </div>
            {executionTimeMs !== null && (
              <div className="stt-cr-summary-card stt-cr-card-meta">
                <div className="stt-cr-card-value">{(executionTimeMs / 1000).toFixed(1)}s</div>
                <div className="stt-cr-card-label">Execution Time</div>
              </div>
            )}
          </div>

          {/* Rule breakdown pills */}
          {Object.keys(summary.byRule).length > 0 && (
            <div className="stt-cr-rule-pills">
              {Object.entries(summary.byRule)
                .sort((a, b) => b[1] - a[1])
                .map(([rule, count]) => (
                  <span
                    key={rule}
                    className={`stt-cr-pill ${filterRule === rule ? 'active' : ''}`}
                    onClick={() => setFilterRule(filterRule === rule ? '' : rule)}
                    title={`Click to filter by ${rule}`}
                  >
                    {rule.replace(/_/g, ' ')}: {count}
                  </span>
                ))}
            </div>
          )}

          {/* Inline Filters + Export */}
          <div className="stt-cr-table-controls">
            <div className="stt-cr-inline-filters">
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value as any)}
              >
                <option value="all">All Severities</option>
                <option value="blocker">Blockers Only</option>
                <option value="warning">Warnings Only</option>
              </select>
              <select
                value={filterRule}
                onChange={(e) => setFilterRule(e.target.value)}
              >
                <option value="">All Rules</option>
                {availableRules.map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-secondary"
              onClick={exportExcel}
              disabled={loading || summary.totalViolations === 0}
            >
              Export Excel
            </button>
          </div>

          {/* Results Table */}
          {filteredViolations.length > 0 ? (
            <div className="stt-cr-table-wrapper">
              <table className="stt-cr-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}></th>
                    <th>Rule</th>
                    <th>Slot ID</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Template</th>
                    <th>Instructor</th>
                    <th>Room</th>
                    <th>Course / Topic</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredViolations.map((v, idx) => {
                    const c = v.conflicting;
                    const cTimeRange = c && c.slotStartTime && c.slotEndTime
                      ? `${c.slotStartTime}-${c.slotEndTime}`
                      : '';
                    const cCourseTopic = c
                      ? `${c.courseName || ''}${c.topicName ? ` / ${c.topicName}` : ''}`.trim()
                      : '';
                    return (
                      <tr key={`${v.slotId}-${v.ruleKey}-${idx}`} className={`stt-cr-row-${v.severity}`}>
                        <td>
                          <span
                            className={`stt-cr-severity-dot stt-cr-dot-${v.severity}`}
                            title={v.severity}
                          />
                        </td>
                        <td className="stt-cr-rule-cell">{v.ruleLabel}</td>
                        <td className="stt-cr-slot-id-cell">
                          {v.slotId ? <span title="Slot ID">#{v.slotId}</span> : '-'}
                          {c && (
                            <div className="stt-cr-conflicting-slot" title="Conflicting slot">
                              ↔ #{c.slotId}
                            </div>
                          )}
                        </td>
                        <td>
                          <div>{v.slotDate}</div>
                          {c && c.slotDate && c.slotDate !== v.slotDate && (
                            <div className="stt-cr-conflicting-line">↔ {c.slotDate}</div>
                          )}
                        </td>
                        <td>
                          <div>{v.slotStartTime && v.slotEndTime ? `${v.slotStartTime}-${v.slotEndTime}` : '-'}</div>
                          {cTimeRange && (
                            <div className="stt-cr-conflicting-line">↔ {cTimeRange}</div>
                          )}
                        </td>
                        <td>
                          <div>{v.templateName || '-'}</div>
                          {c && c.templateName && c.templateName !== v.templateName && (
                            <div className="stt-cr-conflicting-line">↔ {c.templateName}</div>
                          )}
                        </td>
                        <td>
                          <div>{v.instructorName || '-'}</div>
                          {c && (c.instructorName || null) !== (v.instructorName || null) && (
                            <div className="stt-cr-conflicting-line">↔ {c.instructorName || '-'}</div>
                          )}
                        </td>
                        <td>
                          <div>{v.roomName || '-'}</div>
                          {c && (c.roomName || null) !== (v.roomName || null) && (
                            <div className="stt-cr-conflicting-line">↔ {c.roomName || '-'}</div>
                          )}
                        </td>
                        <td>
                          <div>
                            {v.courseName || ''}
                            {v.topicName ? ` / ${v.topicName}` : ''}
                          </div>
                          {cCourseTopic && cCourseTopic !== `${v.courseName || ''}${v.topicName ? ` / ${v.topicName}` : ''}`.trim() && (
                            <div className="stt-cr-conflicting-line">↔ {cCourseTopic}</div>
                          )}
                        </td>
                        <td className="stt-cr-message-cell">{v.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="stt-cr-no-results">
              {summary.totalViolations === 0
                ? 'No violations found in the selected date range.'
                : 'No violations match the current filter.'}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="stt-cr-pagination">
              <button
                disabled={pagination.page <= 1 || loading}
                onClick={() => runReport(pagination.page - 1)}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
                {' '}({pagination.totalCount} total)
              </span>
              <button
                disabled={pagination.page >= pagination.totalPages || loading}
                onClick={() => runReport(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!hasRun && !loading && (
        <div className="stt-cr-empty-state">
          Select a date range and click "Run Report" to scan for scheduling conflicts across all templates.
          <br />
          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>Maximum range: 1 year</span>
        </div>
      )}
    </div>
  );
};

export default ConflictReport;
